import React, { useState } from "react";

import { animated } from "@react-spring/web";

import Box from "@mui/material/Box";
import styled from "@mui/material/styles/styled";

import { Row } from "@rui/Stack";

import ScrollSliderButton from "./ScrollSliderButton";
import type { ScrollSliderProps, UseScrollSlider } from "./types";
import useScrollSlider from "./useScrollSlider";

const Scroller = styled(animated(Box))`
  position: relative;
  display: inline-block;
  flex: 1 1 auto;
  white-space: normal;
  // Hide dimensionless scrollbar on MacOS
  scrollbar-width: none; // Firefox
  &::-webkit-scrollbar {
    display: none; // Safari + Chrome
  }
  overflow-x: auto;
  overflow-y: hidden;
  width: 100%;
  overscroll-behavior-x: contain;
`;

/** Scrollable content; can be used on its own if paginator buttons are different than the default */
export const ScrollSliderContent = ({
  children,
  listRef,
  onWheel,
  scroll,
  scrollerRef,
  spacing = "12px",
}: {
  children: ScrollSliderProps["children"];
  listRef: UseScrollSlider["listRef"];
  onWheel: UseScrollSlider["onWheel"];
  scroll: UseScrollSlider["scroll"];
  scrollerRef: UseScrollSlider["scrollerRef"];
  spacing?: ScrollSliderProps["spacing"];
}) => (
  <Scroller ref={scrollerRef} scrollLeft={scroll} onWheel={onWheel}>
    <Row spacing={spacing} ref={listRef} sx={{ paddingRight: spacing }}>
      {children}
      {/* shim to add spacing at the end of the last child */}
      <div>&nbsp;</div>
    </Row>
  </Scroller>
);

/** Scroll slider component w/ buttons */
const ScrollSlider = ({
  children,
  selected,
  spacing = "12px",
}: ScrollSliderProps) => {
  const [showScroll, setShowScroll] = useState(false);
  const {
    canScrollBackward,
    canScrollForward,
    onScrollClick,
    ...contentProps
  } = useScrollSlider({ selected });

  return (
    <Box
      sx={{ position: "relative" }}
      onMouseOver={() => setShowScroll(true)}
      onMouseOut={() => setShowScroll(false)}
    >
      <ScrollSliderContent {...contentProps} spacing={spacing}>
        {children}
      </ScrollSliderContent>
      {canScrollBackward && (
        <ScrollSliderButton
          direction="backward"
          show={showScroll}
          onClick={onScrollClick}
        />
      )}
      {canScrollForward && (
        <ScrollSliderButton
          direction="forward"
          show={showScroll}
          onClick={onScrollClick}
        />
      )}
    </Box>
  );
};

export default ScrollSlider;
